import { createContext, useContext, useEffect, useMemo, ReactNode } from "react";
import { useAppSettings } from "@/hooks/useAppSettings";
import { useTheme } from "@/contexts/ThemeContext";

// ── Default values ──────────────────────────────────────────────
export const DEFAULT_COLORS: Record<string, string> = {
  background: "270 20% 98%",
  foreground: "270 30% 15%",
  card: "0 0% 100%",
  "card-foreground": "270 30% 15%",
  primary: "152 60% 42%",
  "primary-foreground": "0 0% 100%",
  secondary: "152 30% 94%",
  "secondary-foreground": "152 30% 25%",
  muted: "152 15% 93%",
  "muted-foreground": "152 10% 50%",
  accent: "30 85% 55%",
  "accent-foreground": "0 0% 100%",
  border: "152 15% 90%",
  ring: "152 60% 42%",
  "tab-active": "152 60% 42%",
  "tab-inactive": "152 10% 60%",
};

export const COLOR_LABELS: Record<string, string> = {
  background: "Fundo",
  foreground: "Texto principal",
  card: "Fundo dos cards",
  "card-foreground": "Texto dos cards",
  primary: "Cor primária",
  "primary-foreground": "Texto da cor primária",
  secondary: "Cor secundária",
  "secondary-foreground": "Texto da cor secundária",
  muted: "Fundo atenuado",
  "muted-foreground": "Texto atenuado",
  accent: "Cor de destaque",
  "accent-foreground": "Texto de destaque",
  border: "Bordas",
  ring: "Anel de foco",
  "tab-active": "Aba ativa",
  "tab-inactive": "Aba inativa",
};

export const DEFAULT_TEXTS: Record<string, string> = {
  app_name: "Glucofy",
  login_subtitle: "Ingresa tu correo para comenzar",
  login_button: "Entrar",
  login_loading: "Verificando...",
  install_button: "Instalar",
  home_greeting: "Hola",
  home_subtitle: "Explora tus contenidos",
  search_placeholder: "Buscar contenido...",
  continue_reading: "Continuar leyendo",
  profile_title: "Tu Perfil",
  logout_button: "Cerrar sesión",
  recetas_title: "Recetas",
  recetas_subtitle: "Elige una categoría",
  community_title: "Comunidad",
  community_subtitle: "Próximamente",
  menu_home: "Inicio",
  menu_recetas: "Recetas",
  menu_community: "Comunidad",
  menu_profile: "Perfil",
  menu_categorias: "Categorías",
  menu_admin: "Admin",
};

export const TEXT_LABELS: Record<string, string> = {
  app_name: "Nome do app",
  login_subtitle: "Subtítulo do login",
  login_button: "Botão de entrar",
  login_loading: "Texto carregando",
  install_button: "Botão instalar",
  home_greeting: "Saudação da home",
  home_subtitle: "Subtítulo da home",
  search_placeholder: "Placeholder da busca",
  continue_reading: "Continuar lendo",
  profile_title: "Título do perfil",
  logout_button: "Botão sair",
  recetas_title: "Título das Receitas",
  recetas_subtitle: "Subtítulo das Receitas",
  community_title: "Título Comunidade",
  community_subtitle: "Subtítulo Comunidade",
  menu_home: "Menu: Início",
  menu_recetas: "Menu: Receitas",
  menu_community: "Menu: Comunidade",
  menu_profile: "Menu: Perfil",
  menu_categorias: "Menu: Categorías",
  menu_admin: "Menu: Admin",
};

export type MenuItemConfig = {
  id: string;
  visible: boolean;
};

export const DEFAULT_MENU_ORDER: MenuItemConfig[] = [
  { id: "home", visible: true },
  { id: "recetas", visible: true },
  { id: "community", visible: true },
  { id: "profile", visible: true },
];

export const DEFAULT_HOME_SECTIONS: string[] = [
  "banners",
  "reposteria_cero",
  "search",
  "continue_reading",
  "products",
];

export const HOME_SECTION_LABELS: Record<string, string> = {
  banners: "Carrossel de Banners",
  reposteria_cero: "Banner Repostería Cero",
  search: "Barra de Busca",
  continue_reading: "Continuar Lendo",
  products: "Grade de Produtos",
};

// ── Context ─────────────────────────────────────────────────────
interface AppConfig {
  colors: Record<string, string>;
  texts: Record<string, string>;
  menuOrder: MenuItemConfig[];
  homeSections: string[];
  loading: boolean;
}

const AppConfigContext = createContext<AppConfig>({
  colors: DEFAULT_COLORS,
  texts: DEFAULT_TEXTS,
  menuOrder: DEFAULT_MENU_ORDER,
  homeSections: DEFAULT_HOME_SECTIONS,
  loading: true,
});

export const useAppConfig = () => useContext(AppConfigContext);

// Helper to get text with fallback
export const useText = (key: string) => {
  const { texts } = useAppConfig();
  return texts[key] ?? DEFAULT_TEXTS[key] ?? key;
};

// ── Provider ────────────────────────────────────────────────────
export const AppConfigProvider = ({ children }: { children: ReactNode }) => {
  const { data: settings, isLoading } = useAppSettings();

  const colors = useMemo(() => {
    if (!settings?.theme_colors) return DEFAULT_COLORS;
    try { return { ...DEFAULT_COLORS, ...JSON.parse(settings.theme_colors) }; } catch { return DEFAULT_COLORS; }
  }, [settings?.theme_colors]);

  const texts = useMemo(() => {
    if (!settings?.app_texts) return DEFAULT_TEXTS;
    try { return { ...DEFAULT_TEXTS, ...JSON.parse(settings.app_texts) }; } catch { return DEFAULT_TEXTS; }
  }, [settings?.app_texts]);

  const menuOrder = useMemo(() => {
    const VALID_IDS = ["home", "recetas", "community", "profile"];
    const LEGACY_MAP: Record<string, string> = { feed: "recetas" };
    let parsed: MenuItemConfig[] = DEFAULT_MENU_ORDER;
    if (settings?.menu_config) {
      try { parsed = JSON.parse(settings.menu_config); } catch { /* use default */ }
    }
    parsed = parsed.map((item) => ({ ...item, id: LEGACY_MAP[item.id] || item.id }));
    for (const id of VALID_IDS) {
      if (!parsed.find((item) => item.id === id)) parsed.push({ id, visible: true });
    }
    return parsed.filter((item) => VALID_IDS.includes(item.id));
  }, [settings?.menu_config]);

  const homeSections = useMemo(() => {
    if (!settings?.home_layout) return DEFAULT_HOME_SECTIONS;
    try { return JSON.parse(settings.home_layout); } catch { return DEFAULT_HOME_SECTIONS; }
  }, [settings?.home_layout]);

  // Apply CSS variables dynamically and cache them — but only in light mode
  // Estas cores vêm do painel de admin e são anteriores ao tema claro/escuro.
  // Estilo em linha ganha de qualquer regra de folha por especificidade, então
  // aplicá-las sempre apagaria silenciosamente a paleta escura do index.css.
  // Por isso elas só valem no tema claro.
  //
  // A condição do desenho anterior saiu junto com ele: existia um só desenho e
  // um só lugar onde essas cores podiam atrapalhar.
  const { theme } = useTheme();
  useEffect(() => {
    const root = document.documentElement;
    if (theme === "dark") {
      Object.keys(colors).forEach((key) => {
        root.style.removeProperty(`--${key}`);
      });
      return;
    }
    Object.entries(colors).forEach(([key, value]) => {
      root.style.setProperty(`--${key}`, String(value));
    });
    // Cache colors in localStorage for instant application on next load
    localStorage.setItem('app_theme_colors', JSON.stringify(colors));
    return () => {
      Object.keys(colors).forEach((key) => {
        root.style.removeProperty(`--${key}`);
      });
    };
  }, [colors, theme]);

  return (
    <AppConfigContext.Provider value={{ colors, texts, menuOrder, homeSections, loading: isLoading }}>
      {children}
    </AppConfigContext.Provider>
  );
};
